import type { HTTP } from "@/core/http";
import {
  isAdminRoleName,
  isProjectManagerRoleName,
} from "@/modules/auth/utils/roles";
import type {
  PlatformUserListItem,
  ProjectApiResponse,
  ProjectByConversationApiResponse,
  ProjectCreateRequest,
  ProjectCreateResponse,
  ProjectHistoryApiResponse,
  ProjectMetadataApiResponse,
  ProjectUpdateRequest,
} from "@/shared/api";
import type { Project, ProjectManagerOption } from "../types";

export class ProjectService {
  constructor(private readonly httpClient: HTTP) {}

  /** GET /pms/projects — optional status filter (All / Draft / Open / On Hold / Closed). */
  async listProjects(status?: string): Promise<Project[]> {
    const params =
      status && status !== "ALL"
        ? `?status=${encodeURIComponent(status)}`
        : "";
    const result = await this.httpClient.get<ProjectApiResponse[]>(
      `/pms/projects${params}`,
    );
    return result ?? [];
  }

  /** GET /pms/projects/{projectId} */
  async getProject(projectId: number): Promise<Project> {
    return this.httpClient.get<ProjectApiResponse>(
      `/pms/projects/${projectId}`,
    );
  }

  /** GET /pms/projects/conversation/{conversationUuid} */
  async getByConversation(
    conversationUuid: string,
  ): Promise<ProjectByConversationApiResponse> {
    return this.httpClient.get<ProjectByConversationApiResponse>(
      `/pms/projects/conversation/${conversationUuid}`,
    );
  }

  /** POST /pms/projects */
  async createProject(
    data: ProjectCreateRequest,
  ): Promise<ProjectCreateResponse> {
    return this.httpClient.post<ProjectCreateResponse, ProjectCreateRequest>(
      "/pms/projects",
      {
        body: data,
      },
    );
  }

  /** PUT /pms/projects/{projectId} */
  async updateProject(
    projectId: number,
    data: ProjectUpdateRequest,
  ): Promise<Project> {
    return this.httpClient.put<ProjectApiResponse, ProjectUpdateRequest>(
      `/pms/projects/${projectId}`,
      { body: data },
    );
  }

  /** GET /pms/projects/{projectId}/history */
  async getProjectHistory(
    projectId: number,
  ): Promise<ProjectHistoryApiResponse[]> {
    const result = await this.httpClient.get<ProjectHistoryApiResponse[]>(
      `/pms/projects/${projectId}/history`,
    );
    return result ?? [];
  }

  /** GET /pms/projects/metadata */
  async getProjectsMetadata(): Promise<ProjectMetadataApiResponse> {
    return this.httpClient.get<ProjectMetadataApiResponse>(
      "/pms/projects/metadata",
    );
  }

  /** Users eligible as project managers (Admin or Project Manager roles). */
  async listProjectManagers(): Promise<ProjectManagerOption[]> {
    const users = await this.httpClient.get<PlatformUserListItem[]>(
      "/platform/users",
    );

    return (users ?? [])
      .filter((user) =>
        (user.roles ?? []).some((role) => {
          const roleName = role.roleName;
          return (
            isAdminRoleName(roleName) || isProjectManagerRoleName(roleName)
          );
        }),
      )
      .map((user) => ({
        id: user.userId,
        name: user.name,
        email: user.email,
      }));
  }
}
